import { FormGroup, FormHelperText, Stack, TextField } from "@mui/material";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { z } from "zod";
import PasswordRules from "../../components/auth/PasswordRules/PasswordRules";
import DialogForm from "../../components/common/DialogForm/DialogForm";
import passwordSchema from "../../helpers/auth/passwordSchema";
import defaultFormConfig from "../../helpers/validation/defaultFormConfig";
import { useGetRoles } from "../../hooks/useRole";
import Role, { roleSchema } from "../../types/role/Role";
import User from "../../types/user/User";
import RolesSelect from "../../components/roles/RolesSelect";
import { useTranslation } from "react-i18next";

const FORM_ID = "user-form";

export type UserFormValues = {
  firstName: string;
  lastName: string;
  email?: string;
  role: Role | null;
  password?: string;
  confirmPassword?: string;
};

export type UserAction = "Create" | "Edit";

export type UserFormProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  createUser: (formData: UserFormValues) => Promise<void>;
  editUser: (formData: UserFormValues) => Promise<void>;
  selectedUser: User | null;
  action?: UserAction;
};

const DEFAULT_VALUES: UserFormValues = {
  email: "",
  firstName: "",
  lastName: "",
  role: null,
  password: "",
  confirmPassword: "",
};

const createSchema = z
  .object({
    email: z
      .string()
      .email({ message: "Please specify a valid email." })
      .min(1, {
        message: "Email is required.",
      }),
    firstName: z.string().min(1, {
      message: "First name is required.",
    }),
    lastName: z.string().min(1, {
      message: "Last name is required.",
    }),
    role: roleSchema,
    password: passwordSchema("Password"),
    confirmPassword: z.string().min(1, {
      message: "Please confirm the password.",
    }),
  })
  .refine(
    ({ password, confirmPassword }) => {
      return password === confirmPassword;
    },
    {
      message: "Passwords do not match.",
      path: ["confirmPassword"],
    }
  );

const editSchema = z.object({
  firstName: z.string().min(1, {
    message: "First name is required.",
  }),
  lastName: z.string().min(1, {
    message: "Last name is required.",
  }),
  role: roleSchema,
});

const schemaByAction = {
  Create: createSchema,
  Edit: editSchema,
};

const UserForm = ({
  isOpen,
  setIsOpen,
  createUser,
  editUser,
  selectedUser,
  action = "Create",
}: UserFormProps) => {
  const { customerId } = useParams();
  const { data: roles, isLoading: isRolesLoading } = useGetRoles(customerId);
  const {
    formState,
    watch,
    control,
    register,
    handleSubmit,
    reset,
    getValues,
  } = useForm<UserFormValues>({
    ...defaultFormConfig<UserFormValues>(schemaByAction[action]),
    defaultValues: DEFAULT_VALUES,
  });
  const { t } = useTranslation(["common", "users", "errors", "auth"]);
  const { isSubmitting, isDirty, isValid, errors } = formState;

  const renderCreateFields = () => (
    <>
      <FormGroup>
        <TextField
          type="email"
          label={t("users:email")}
          error={!!errors.email}
          {...register("email", {
            required: t("errors:emailRequired"),
          })}
          required
        />
        {!!errors.email && (
          <FormHelperText error>
            {errors.email.message as string}
          </FormHelperText>
        )}
      </FormGroup>
      <FormGroup>
        <TextField
          type="password"
          label={t("users:temporaryPassword")}
          error={!!errors.password}
          {...register("password", {
            required: t("errors:temporaryPasswordRequired"),
          })}
          required
        />
        {!!errors.password && (
          <FormHelperText error>
            {errors.password.message as string}
          </FormHelperText>
        )}
      </FormGroup>
      <FormGroup>
        <TextField
          type="password"
          label={t("auth:confirmPassword")}
          error={!!errors.confirmPassword}
          {...register("confirmPassword", {
            required: t("errors:confirmPasswordRequired"),
            validate: (value) =>
              value === getValues("password") ||
              t("errors:passwordsDoNotMatch"),
          })}
          required
        />
        {!!errors.confirmPassword && (
          <FormHelperText error>
            {errors.confirmPassword.message as string}
          </FormHelperText>
        )}
      </FormGroup>
      <PasswordRules
        password={watch("password")}
        confirmPassword={watch("confirmPassword")}
      />
    </>
  );

  const onSubmit = async (formValues: UserFormValues) => {
    if (action === "Create") {
      await createUser(formValues);
    } else {
      await editUser(formValues);
    }
    setIsOpen(false);
  };

  useEffect(() => {
    if (!isOpen || action !== "Edit" || !selectedUser) return;
    reset({
      ...selectedUser,
      role: roles?.find((role: Role) => role.roleId === selectedUser.roleId),
    });
  }, [action, isOpen, reset, roles, selectedUser]);

  return (
    <DialogForm
      formId={FORM_ID}
      dialogTitle={`${t("common:" + action.toLowerCase())} ${t("common:user")}`}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      isSubmitting={isSubmitting}
      isCancelDisabled={isSubmitting}
      isSubmitDisabled={!isDirty || !isValid}
      afterClose={reset}
    >
      <form id={FORM_ID} onSubmit={handleSubmit(onSubmit)} noValidate>
        <Stack marginTop={1} spacing={2}>
          <div className="flex space-x-4">
            <FormGroup className="flex-grow">
              <TextField
                label={t("users:firstName")}
                error={!!errors.firstName}
                {...register("firstName", {
                  required: t("errors:firstNameRequired"),
                })}
                required
              />
              {!!errors.firstName && (
                <FormHelperText error>
                  {errors.firstName.message as string}
                </FormHelperText>
              )}
            </FormGroup>
            <FormGroup className="flex-grow">
              <TextField
                label={t("users:lastName")}
                error={!!errors.lastName}
                {...register("lastName", {
                  required: t("errors:lastNameRequired"),
                })}
                required
              />
              {!!errors.lastName && (
                <FormHelperText error>
                  {errors.lastName.message as string}
                </FormHelperText>
              )}
            </FormGroup>
          </div>
          {action === "Create" && renderCreateFields()}
          <RolesSelect
            control={control}
            roles={roles}
            isRolesLoading={isRolesLoading}
            error={errors.role?.message}
            defaultValue={DEFAULT_VALUES.role}
          />
        </Stack>
      </form>
    </DialogForm>
  );
};

export default UserForm;
